import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { acknowledgeCue, getCustomerCue } from '../api/endpoints/cues';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import type { BusinessCustomerSummary } from '../api/schemas';
import { Drawer } from '../admin/components/Drawer';
import { useSession } from '../auth/useSession';
import { usePermissions } from '../auth/usePermissions';
import { customerStatus, monthYear, relativeVisit } from '../lib/customers';
import { panamaToday, pauseState } from '../lib/customerFile';
import { useCustomerProfiles } from '../lib/useCustomerProfiles';
import { formatDateTime } from '../lib/format';
import { CueBanner } from './CueBanner';
import { CustomerFile } from './CustomerFile';
import { CustomerSubscriptions } from './CustomerSubscriptions';
import { useCapability } from '../lib/useCapability';
import { useTools } from '../lib/useTools';

type CustomerDrawerProps = {
  customer: BusinessCustomerSummary | null;
  onClose: () => void;
};

type FileTab = 'resumen' | 'ficha' | 'planes';
const TAB_LABEL: Readonly<Record<FileTab, string>> = { resumen: 'Resumen', ficha: 'Ficha y notas', planes: 'Planes' };

/** The file's tabs, like the Helios CRM's side file: one pill per part, the open one filled. */
function FileTabs({ tabs, active, onPick }: { tabs: readonly FileTab[]; active: FileTab; onPick: (tab: FileTab) => void }) {
  return (
    <div className="hs-pills customer-file-tabs" role="tablist" aria-label="Partes de la ficha">
      {tabs.map((tab) => (
        <button
          key={tab}
          type="button"
          role="tab"
          aria-selected={tab === active}
          data-status={tab === active ? 'active' : undefined}
          className="hs-pill"
          onClick={() => onPick(tab)}
        >
          {TAB_LABEL[tab]}
        </button>
      ))}
    </div>
  );
}

/**
 * The client's file: the numbers the list already carries, plus the one thing
 * the list is not allowed to show — the mediated cue, fetched on demand
 * through the audited route.
 */
export function CustomerDrawer({ customer, onClose }: CustomerDrawerProps) {
  const { session } = useSession();
  const sellsPlans = usePermissions().can('cobrar');
  const queryClient = useQueryClient();
  const acknowledgeKey = useRef(createIdempotencyKeyHolder()).current;
  const businessId = session?.businessId;

  const cueQuery = useQuery({
    queryKey: ['customer-cue', customer?.customerId],
    queryFn: () => getCustomerCue(customer!.customerId, businessId!),
    enabled: Boolean(customer && businessId),
  });
  const cue = cueQuery.data?.cue;

  const acknowledgeMutation = useMutation({
    mutationFn: () => acknowledgeCue(cue!.bookingId, cue!.cueId, acknowledgeKey.ensure()),
    onSuccess: () => {
      queryClient.setQueryData(['customer-cue', customer?.customerId], { cue: null });
    },
    onSettled: () => acknowledgeKey.reset(),
  });

  const now = new Date();
  // The same profile read Clientes already made: a pause the team set reads En pausa here too.
  const profiles = useCustomerProfiles();
  const pause = customer ? (profiles.get(customer.customerId)?.pause ?? null) : null;
  // What a coach must not miss before class, right under the name instead of below the figures.
  const injuries = customer ? (profiles.get(customer.customerId)?.injuries ?? null) : null;
  const status = customer ? customerStatus(customer, now, pauseState(pause, panamaToday(now)) === 'active') : null;
  // Every type sells its planes or paquetes when the tool is on (Herramientas) and the plan opens it.
  const sellsPlansHere = useCapability('planes');
  const { tools } = useTools();
  const showsPlans = tools.planes && sellsPlans && sellsPlansHere;
  const tabs: FileTab[] = showsPlans ? ['resumen', 'ficha', 'planes'] : ['resumen', 'ficha'];
  const [picked, setPicked] = useState<{ customerId: string; tab: FileTab } | null>(null);
  // A different client opens on Resumen again.
  const tab = customer && picked?.customerId === customer.customerId && tabs.includes(picked.tab) ? picked.tab : 'resumen';

  return (
    <Drawer open={Boolean(customer)} title={customer?.displayName ?? ''} onClose={onClose}>
      {customer ? (
        <>
          <p className="drawer-facts">
            {[
              status?.label,
              customer.maskedPhone,
              `cliente desde ${monthYear(customer.firstVisitAt)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {injuries ? (
            <p className="drawer-injuries">
              <span className="status" data-tone="hold">
                Lesiones
              </span>{' '}
              {injuries}
            </p>
          ) : null}
          <FileTabs tabs={tabs} active={tab} onPick={(next) => setPicked({ customerId: customer.customerId, tab: next })} />
          {tab === 'resumen' ? (
            <>
              <CueBanner
                cue={cue}
                onAcknowledge={() => acknowledgeMutation.mutate()}
                acknowledging={acknowledgeMutation.isPending}
              />
              <div className="pnl-figures customer-figures">
                <div className="pnl-figure">
                  <p className="label">Visitas completadas</p>
                  <p className="value">{customer.completedVisits}</p>
                </div>
                <div className="pnl-figure">
                  <p className="label">Citas próximas</p>
                  <p className="value">{customer.upcomingVisits}</p>
                </div>
                <div className="pnl-figure">
                  <p className="label">No llegó</p>
                  <p className="value">{customer.noShowCount}</p>
                </div>
              </div>
              <ul className="rows">
                <li className="row">
                  <div className="grow">
                    <p className="row-title">Última visita</p>
                    <p className="row-sub">{formatDateTime(customer.lastVisitAt)}</p>
                  </div>
                  <p className="row-sub">{relativeVisit(customer.lastVisitAt, now)}</p>
                </li>
                <li className="row">
                  <div className="grow">
                    <p className="row-title">Primera visita</p>
                    <p className="row-sub">{formatDateTime(customer.firstVisitAt)}</p>
                  </div>
                </li>
              </ul>
            </>
          ) : null}
          {tab === 'ficha' ? <CustomerFile key={customer.customerId} customerId={customer.customerId} /> : null}
          {tab === 'planes' ? <CustomerSubscriptions key={customer.customerId} customerId={customer.customerId} /> : null}
        </>
      ) : null}
    </Drawer>
  );
}
